import React from 'react';
import { Plus, Sparkles } from 'lucide-react';
import { pickUpsell } from '../data/upsells';
import { useTranslation } from '../context/TranslationContext';

/**
 * Cross-sell offer card. Two variants:
 *   variant="compact"  — small inline card used in Checkout Order Summary
 *   variant="hero"     — large prominent card used in the Cart Drawer
 */
function UpsellOffer(props) {
  var cartItems = props.cartItems;
  var allProducts = props.allProducts;
  var addToCart = props.addToCart;
  var variant = props.variant || 'compact';
  var tx = props.tx || {};
  // Use the shopper's selected currency for all price renders so this card
  // doesn't mix USD with the rest of the checkout/cart UI.
  var formatPrice = useTranslation().formatPrice;
  var addedState = React.useState(false);
  var added = addedState[0];
  var setAdded = addedState[1];

  if (!allProducts || allProducts.length === 0 || !cartItems || cartItems.length === 0) return null;
  var upsell = pickUpsell(cartItems, allProducts);
  if (!upsell) return null;
  if (added) return null;

  function handleAdd() {
    addToCart(upsell.product, null, {
      unitPrice: upsell.finalPrice,
      isDigital: upsell.isDigital,
    });
    setAdded(true);
  }

  var savingsPct = Math.round(upsell.discount * 100);
  var image = upsell.product.image || (upsell.product.images && upsell.product.images[0]) || '';

  if (variant === 'hero') {
    return React.createElement('div', {
      'data-testid': 'cart-upsell',
      className: 'mt-4 bg-gradient-to-br from-[#d4af37]/15 via-amber-900/10 to-black border-2 border-[#d4af37]/50 rounded-2xl p-4 shadow-lg shadow-[#d4af37]/10'
    },
      React.createElement('div', { className: 'flex items-center gap-2 mb-3' },
        React.createElement(Sparkles, { className: 'w-4 h-4 text-[#d4af37]' }),
        React.createElement('p', { className: 'text-[#d4af37] text-xs font-bold uppercase tracking-widest' }, tx.upsellTitle || 'Frequently bought together'),
        upsell.discount > 0 ? React.createElement('span', { className: 'ml-auto bg-[#d4af37] text-black text-[10px] font-bold px-2 py-0.5 rounded-full' }, 'SAVE ', savingsPct, '%') : null
      ),
      React.createElement('div', { className: 'flex gap-3 items-start' },
        React.createElement('img', {
          src: image,
          alt: upsell.product.name,
          className: 'w-24 h-24 rounded-xl object-cover flex-shrink-0 border border-white/10'
        }),
        React.createElement('div', { className: 'flex-1 min-w-0' },
          React.createElement('p', { className: 'text-white text-sm font-semibold leading-tight mb-1' }, upsell.product.name),
          upsell.isDigital ? React.createElement('p', { className: 'text-[#d4af37]/90 text-[10px] font-semibold uppercase tracking-wide mb-1' }, '📱 Indexed FILE for ForScore') : null,
          upsell.product.shortDescription ? React.createElement('p', { className: 'text-neutral-400 text-xs leading-snug mb-2 line-clamp-2' }, upsell.product.shortDescription) : null,
          React.createElement('div', { className: 'flex items-baseline gap-2 mb-2' },
            React.createElement('span', { className: 'text-[#d4af37] font-bold text-lg' }, formatPrice(upsell.finalPrice)),
            upsell.discount > 0 ? React.createElement('span', { className: 'text-neutral-500 text-xs line-through' }, formatPrice(upsell.originalPrice)) : null
          )
        )
      ),
      React.createElement('button', {
        onClick: handleAdd,
        'data-testid': 'cart-upsell-add-btn',
        className: 'mt-3 w-full flex items-center justify-center gap-2 bg-[#d4af37] text-black font-bold text-sm px-4 py-3 rounded-full hover:bg-white transition-colors'
      },
        React.createElement(Plus, { className: 'w-4 h-4' }),
        (tx.upsellAdd || 'Add to order'), ' — ', formatPrice(upsell.finalPrice)
      )
    );
  }

  // compact variant (Checkout)
  return React.createElement('div', {
    'data-testid': 'checkout-upsell',
    className: 'mb-4 bg-gradient-to-br from-[#d4af37]/10 to-amber-700/5 border border-[#d4af37]/40 rounded-xl p-3'
  },
    React.createElement('p', { className: 'text-[#d4af37] text-xs font-semibold uppercase tracking-wider mb-2' }, tx.upsellTitle),
    React.createElement('div', { className: 'flex gap-3 items-center' },
      React.createElement('img', {
        src: image,
        alt: upsell.product.name,
        className: 'w-14 h-14 rounded-lg object-cover flex-shrink-0'
      }),
      React.createElement('div', { className: 'flex-1 min-w-0' },
        React.createElement('p', { className: 'text-white text-sm font-medium truncate' }, upsell.product.name),
        upsell.isDigital ? React.createElement('p', { className: 'text-[#d4af37]/80 text-[10px] font-semibold uppercase tracking-wide mt-0.5' }, '📱 Indexed FILE for ForScore') : null,
        React.createElement('div', { className: 'flex items-center gap-2 mt-0.5' },
          React.createElement('span', { className: 'text-[#d4af37] font-bold text-base' }, formatPrice(upsell.finalPrice)),
          upsell.discount > 0 ? React.createElement('span', { className: 'text-neutral-500 text-xs line-through' }, formatPrice(upsell.originalPrice)) : null,
          upsell.discount > 0 ? React.createElement('span', { className: 'text-green-400 text-xs font-semibold' }, '−', savingsPct, '%') : null
        )
      ),
      React.createElement('button', {
        onClick: handleAdd,
        'data-testid': 'upsell-add-btn',
        className: 'flex-shrink-0 flex items-center gap-1 bg-[#d4af37] text-black font-semibold text-xs px-3 py-2 rounded-full hover:bg-white transition-colors whitespace-nowrap'
      },
        React.createElement(Plus, { className: 'w-3 h-3' }),
        (tx.upsellAdd || 'Add'), ' ', formatPrice(upsell.finalPrice)
      )
    )
  );
}

export default UpsellOffer;
